import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { Activity, Trophy } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';

import { formatRelativeTime } from '@/lib/datetime';
import { DEFAULT_TEAM_ICON_COLOR, getTeamIcon } from '@/utils/teamIcons';

interface ActivityItem {
  id: string;
  type: string;
  message: string;
  teamId: string | null;
  teamName: string | null;
  teamIconName?: string;
  teamIconColor?: string;
  challengeId?: string | null;
  challengeName?: string | null;
  points?: number | null;
  reason?: string | null;
  photoUrl?: string | null;
  createdAt: string;
}

interface Team {
  id: string;
  name: string;
  iconName: string;
  iconColor: string;
}

interface EventActivityFeedProps {
  eventId: string | undefined;
  /** Filter to a single team's events. Omit for the full event feed. */
  teamId?: string;
  /** Cap how many entries to fetch + render. */
  limit?: number;
  title?: string;
  description?: string;
  /** Use a tighter layout (no card chrome). For embedding inside other cards. */
  variant?: 'card' | 'plain';
  /** Right-aligned header slot (card variant only) — e.g. a connection badge. */
  headerRight?: ReactNode;
}

function getActivityLabel(type: string): string {
  const labels: Record<string, string> = {
    challenge_completed: 'Completed a challenge',
    team_joined: 'Joined the event',
    points_awarded: 'Points awarded',
    hint_used: 'Used a hint',
    artifact_collected: 'Collected an artifact',
  };
  return labels[type] || type.replace(/_/g, ' ');
}

/**
 * Shared event-activity feed used on both the event Activity tab and
 * inside the team detail Overview/Activity tabs. Same row rendering
 * (team icon + name + relative time + points) so the feed feels
 * consistent across surfaces. Filters to a single team via the
 * `teamId` prop when provided.
 */
export function EventActivityFeed({
  eventId,
  teamId,
  limit = 100,
  title = 'Activity Feed',
  description,
  variant = 'card',
  headerRight,
}: EventActivityFeedProps) {
  const apiClient = useApiClient();

  const { data: activityData, isLoading, isError } = useQuery<{
    success: boolean;
    data: ActivityItem[];
  }>({
    queryKey: ['EventActivity', eventId, 'limit', limit],
    queryFn: () =>
      apiClient.get(`/events/${eventId}/activity`, {
        params: { limit },
      }),
    enabled: !!eventId,
    refetchInterval: 10000,
    staleTime: 5000,
  });

  const { data: teamsData } = useQuery<{ success: boolean; data: Team[] }>({
    queryKey: ['EventTeams', eventId],
    queryFn: () => apiClient.get(`/events/${eventId}/teams`),
    enabled: !!eventId,
    staleTime: 30000,
  });

  // Enrich each activity row with the team's current icon/color so
  // the avatar matches what the listing pages show, and filter to
  // the requested team when scoped.
  const enrichedActivities = useMemo(() => {
    const activities = activityData?.data || [];
    const teams = teamsData?.data || [];
    const teamMap = new Map(teams.map((t) => [t.id, t]));
    const filtered = teamId ? activities.filter((a) => a.teamId === teamId) : activities;
    return filtered.map((activity) => {
      const team = activity.teamId ? teamMap.get(activity.teamId) : null;
      return {
        ...activity,
        teamIconName: activity.teamIconName || team?.iconName,
        teamIconColor: activity.teamIconColor || team?.iconColor,
      };
    });
  }, [activityData?.data, teamsData?.data, teamId]);

  const body = isLoading ? (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-16 rounded-lg" />
      ))}
    </div>
  ) : isError ? (
    // A failed load must not masquerade as "no activity" — say it broke.
    <div className="py-12 text-center text-muted-foreground">
      <Activity className="mx-auto mb-3 h-12 w-12 opacity-30" />
      <p className="font-medium text-foreground">Couldn't load activity</p>
      <p className="text-sm">Check your connection — this feed retries automatically.</p>
    </div>
  ) : enrichedActivities.length === 0 ? (
    <div className="py-12 text-center text-muted-foreground">
      <Activity className="mx-auto mb-3 h-12 w-12 opacity-30" />
      <p className="font-medium">No activity yet</p>
      <p className="text-sm">
        {teamId
          ? 'Activity will appear here as this team participates.'
          : 'Activity will appear here as teams participate.'}
      </p>
    </div>
  ) : (
    <div className="space-y-2">
      {enrichedActivities.map((activity) => {
        const TeamIcon = activity.teamIconName ? getTeamIcon(activity.teamIconName) : null;
        return (
          <div
            key={activity.id}
            className="flex items-start gap-3 rounded-lg border bg-card px-4 py-3 transition-colors hover:bg-muted/50">
            {TeamIcon ? (
              <div
                className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
                style={{ backgroundColor: activity.teamIconColor || DEFAULT_TEAM_ICON_COLOR }}>
                <TeamIcon className="h-4.5 w-4.5 text-white" />
              </div>
            ) : (
              <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-muted">
                <Activity className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {activity.teamName && (
                  <span className="text-sm font-medium text-foreground">{activity.teamName}</span>
                )}
                <span className="text-xs text-muted-foreground">
                  {formatRelativeTime(activity.createdAt)}
                </span>
              </div>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {activity.challengeName
                  ? `${getActivityLabel(activity.type)}: ${activity.challengeName}`
                  : activity.message || getActivityLabel(activity.type)}
              </p>
              {activity.reason && (
                <p className="mt-0.5 text-xs text-muted-foreground">{activity.reason}</p>
              )}
            </div>
            {activity.points != null && activity.points > 0 && (
              <div className="flex flex-shrink-0 items-center gap-1">
                <Trophy className="h-3.5 w-3.5 text-yellow-500" />
                <span className="text-sm font-semibold text-foreground">+{activity.points}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  if (variant === 'plain') return body;

  // Card chrome matches the event Overview tab's "Event Info" panel
  // (`py-0 gap-0` + bordered header + `p-6` body) so this feed sits
  // visually flush with the other cards on those pages.
  return (
    <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
      <CardHeader className="px-6 py-4 !pb-4 border-b">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle>{title}</CardTitle>
            {description && <CardDescription>{description}</CardDescription>}
          </div>
          {headerRight}
        </div>
      </CardHeader>
      <CardContent className="p-6">{body}</CardContent>
    </Card>
  );
}
